import { ControllerParams, CreateControllerFn } from '@wix/yoshi-flow-editor';
import { faqAppStartLoading } from '@wix/bi-logger-labs-data/v2';
import { FAQService } from '../../utils/FAQService';
import { Category } from '@wix/ambassador-faq-category-v2-category/types';
import { ISettingsGetter, IWixStyleParams } from '@wix/tpa-settings';
import settingsParams from './settingsParams';
import constants from '../../constants';
import { QuestionEntry } from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import {
  getQuestionAPI,
  getListCategoriesAPI,
  getListQuestionsAPI,
  getSearchTermAPI,
} from './contollerAPIRequests';

type GetQuestionIdResult = {
  categoryId: string | null | undefined;
  questionId: string | null | undefined;
  fromSearch: boolean;
};
let _api: FAQService;
let _categoryId: string | null | undefined;
let _styleParams: IWixStyleParams;
let isEditor3 = false;

const asyncRequestInitialData = async (
  settings: { get: any },
  api: FAQService,
  qcategoryId: string | null | undefined,
  withErrorHandler: any,
  httpClient: any,
  getListCategories: any,
  getListQuestions: any,
  getIsHaveQuestions: any,
) => {
  let selectedCategory;
  let questionEntries: QuestionEntry[] | null;
  const { showAll, categories } = settings.get(settingsParams.manageCategories);
  const { categories: categoriesList } = await getListCategories();
  const { isHasQuestions = true } = await getIsHaveQuestions();

  const showCategoriesSettingsParam =
    settings.get(settingsParams.displayContent) ===
    constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES;
  const showCategoriesStyleParam =
    _styleParams.booleans.showFAQCategories ?? true;

  const showOneListOfQuestions = !(
    showCategoriesSettingsParam && showCategoriesStyleParam
  );

  let firstId: string | undefined | null = qcategoryId ?? '';
  const validLists = categoriesList?.filter((category: { id?: any }) => {
    const categoryId = category?.id;
    if (showAll) {
      if (typeof firstId === 'undefined' || firstId === '') {
        firstId = categoryId;
      }
      return true;
    }
    if (!categoryId) {
      return false;
    }
    const isValid = categories?.includes(categoryId);
    if (isValid) {
      if (typeof firstId === 'undefined' || firstId === '') {
        firstId = categoryId;
      }
      return true;
    }
  });
  const validCategories = validLists
    ?.filter((category: Category) => category?.id)
    .map((category: Category) => category?.id);
  if (showOneListOfQuestions) {
    selectedCategory = validCategories?.slice(0, 100);
  } else if (firstId) {
    selectedCategory = firstId;
  }
  const result = await getListQuestions({
    categoryId: selectedCategory,
  });
  if (result.questionEntries) {
    questionEntries = result.questionEntries;
  } else {
    questionEntries = [];
  }
  return {
    selectedCategory,
    validCategories,
    questionEntries,
    validLists,
    isHasQuestions,
  };
};

const createController: CreateControllerFn = async ({
  flowAPI,
  controllerConfig,
}: ControllerParams) => {
  _styleParams = controllerConfig.config.style.styleParams;

  const { isSEO = false } = flowAPI.environment;

  const { httpClient, errorHandler, translations } = flowAPI;
  const { t } = translations;
  const { withErrorHandler, getResolvedError } = errorHandler;

  function getUuidCategoryIdFromUrl(url: string): string | null {
    const match = url.match(
      /[?&]categoryId=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:&|$)/,
    );
    return match ? match[1] : null;
  }

  function getUuidQuestionIdFromUrl(url: string): string | null {
    const match = url.match(
      /[?&]questionId=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:&|$)/,
    );
    return match ? match[1] : null;
  }
  function getAppDefIdFromUrl(url: string): string | null {
    const match = url.match(
      /[?&]appDefId=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:&|$)/i,
    );
    return match ? match[1] : null;
  }

  const getQuestionId = async (): Promise<GetQuestionIdResult> => {
    try {
      const appID = controllerConfig.appParams.appDefinitionId;
      const url = controllerConfig.wixCodeApi.location.url;
      const appDefId = getAppDefIdFromUrl(url);
      const isFAQAppDefId = appDefId === appID;
      const questionId = isFAQAppDefId ? getUuidQuestionIdFromUrl(url) : null;
      if (!questionId) {
        return {
          categoryId: null,
          questionId: null,
          fromSearch: false,
        };
      }
      const data = await withErrorHandler(
        () => getQuestionAPI({ id: questionId, httpClient }),
        {
          errorCodesMap: {
            applicationError: {
              INVALID_DATA: () => ({
                message: t('settings.error.invalid.data'),
              }),
            },
            serverError: () => ({
              message: t('settings.error.server.error'),
            }),
          },
        },
      );
      controllerConfig.setProps({
        questionIdReponse: data,
      });
      // TODO add error handler in the widget

      return {
        categoryId: data.questionEntries?.[0].categoryId,
        questionId: data.questionEntries?.[0].id,
        fromSearch: true,
      };
    } catch (error) {
      const resolvedError = getResolvedError(error);
      controllerConfig.setProps({
        fetchQuestionIdResponseError: resolvedError,
      });
      throw error;
    }
  };

  const getListCategories = async () => {
    try {
      const data = await withErrorHandler(
        () => getListCategoriesAPI(httpClient),
        {
          errorCodesMap: {
            applicationError: {
              INVALID_DATA: () => ({
                message: t('widget.invalid.data.error.handler'),
              }),
            },
            serverError: () => ({
              message: t('widget.server.error.handler'),
            }),
          },
        },
      );
      controllerConfig.setProps({
        listCategoriesResponse: data,
        fetchError: null,
      });
      return data;
    } catch (error) {
      const resolvedError = getResolvedError(error);
      controllerConfig.setProps({
        fetchError: resolvedError,
      });
      throw error;
    }
  };
  const getListQuestions = async ({
    categoryId,
    cursor,
    limit,
    prev = [],
  }: {
    categoryId: any;
    cursor: any;
    limit: any;
    prev: QuestionEntry[];
  }) => {
    try {
      const data = await withErrorHandler(
        () =>
          getListQuestionsAPI({ categoryId, httpClient, cursor, limit, isSEO }),
        {
          errorCodesMap: {
            applicationError: {
              INVALID_DATA: () => ({
                message: t('widget.list.questions.invalid.data.error'),
              }),
            },
            serverError: () => ({
              message: t('widget.list.questions.server.error'),
            }),
          },
        },
      );

      controllerConfig.setProps({
        listQuestionsResponse: data,
        initialData: {
          questionEntries: [...prev, ...(data.questionEntries || [])],
          pagingMetadata: data.pagingMetadata,
        },
        fetchError: null,
      });
      return data;
    } catch (error) {
      const resolvedError = getResolvedError(error);
      controllerConfig.setProps({
        fetchError: resolvedError,
      });
      throw error;
    }
  };

  const getIsHaveQuestions = async () => {
    try {
      const data = await withErrorHandler(
        () => getListQuestionsAPI({ httpClient, limit: 1 }),
        {
          errorCodesMap: {
            applicationError: {
              INVALID_DATA: () => ({
                message: t('widget.list.questions.invalid.data.error'),
              }),
            },
            serverError: () => ({
              message: t('widget.list.questions.server.error'),
            }),
          },
        },
      );

      controllerConfig.setProps({
        isHasQuestions:
          data.questionEntries && data.questionEntries?.length > 0,
        fetchError: null,
      });
      return data;
    } catch (error) {
      const resolvedError = getResolvedError(error);
      controllerConfig.setProps({
        fetchError: resolvedError,
      });
      throw error;
    }
  };

  const getSearchTerm = async ({
    cursor,
    term,
    prev = [],
  }: {
    cursor?: any;
    term: any;
    prev?: QuestionEntry[];
  }) => {
    try {
      if (!term) {
        controllerConfig.setProps({
          searchTermResponse: null,
        });
        const initialData = await getInitialData(
          flowAPI.settings,
          _api,
          _categoryId,
        );
        controllerConfig.setProps({
          initialData,
        });

        return;
      }

      const data = await withErrorHandler(
        () => getSearchTermAPI({ term, httpClient, cursor }),
        {
          errorCodesMap: {
            applicationError: {
              INVALID_DATA: () => ({
                message: t('widget.invalid.data.error.handler'),
              }),
            },
            serverError: () => ({
              message: t('widget.server.error.handler'),
            }),
          },
        },
      );
      const questionEntries = [...prev, ...(data.questionEntries || [])];
      controllerConfig.setProps({
        searchTermResponse: { ...data, questionEntries },
        initialData: {
          questionEntries,
          pagingMetadata: data.pagingMetadata,
          validCategories: null,
          selectedCategory: null,
          validLists: null,
        },
      });
      return data;
    } catch (error) {
      const resolvedError = getResolvedError(error);
      controllerConfig.setProps({
        fetchSearchTermError: resolvedError,
      });
      throw error;
    }
  };

  const getCategoryIdFromURL = () => {
    const appID = controllerConfig.appParams.appDefinitionId;
    const url = controllerConfig.wixCodeApi.location.url;
    const appDefId = getAppDefIdFromUrl(url);
    const isFAQAppDefId = appDefId === appID;
    const categoryId = isFAQAppDefId ? getUuidCategoryIdFromUrl(url) : null;

    return {
      categoryId,
      fromSearch: categoryId !== null,
    };
  };

  const getInitialData = async (
    settings: { get: ISettingsGetter },
    faqService: FAQService,
    categoryId: string | null | undefined,
  ) => {
    let result;
    try {
      result = await asyncRequestInitialData(
        settings,
        faqService,
        categoryId,
        withErrorHandler,
        httpClient,
        getListCategories,
        getListQuestions,
        getIsHaveQuestions,
      );
    } catch (e) {
      result = {
        validLists: null,
        selectedCategory: null,
        validCategories: null,
        questionEntries: null,
      };
    }
    return result;
  };

  const onPageReady = async () => {
    try {
      const { appParams, wixCodeApi } = controllerConfig;
      const { instance, instanceId } = appParams;
      const { url } = wixCodeApi.location;
      const baseURL = url;
      _api = new FAQService(
        instance,
        instanceId,
        wixCodeApi?.window?.multilingual,
      );

      let questionIdResult: GetQuestionIdResult;

      try {
        questionIdResult = await getQuestionId();
      } catch (error) {
        console.log('Failed to fetch question ID:', error);
        questionIdResult = {
          categoryId: null,
          questionId: null,
          fromSearch: false,
        };
      }

      let { questionId } = questionIdResult;
      let { categoryId, fromSearch } = questionIdResult;
      if (!categoryId && isSEO) {
        const { categoryId: categoryIdFromURL, fromSearch: fromSearchFromURL } =
          getCategoryIdFromURL();
        if (categoryIdFromURL) {
          categoryId = categoryIdFromURL;
          fromSearch = fromSearchFromURL;
        }
      }
      _categoryId = categoryId;

      const initialData = await getInitialData(
        flowAPI.settings,
        _api,
        categoryId,
      );

      if (initialData.validLists?.length !== 0 && !questionId) {
        const showFirstAnswer = flowAPI.settings.get(
          settingsParams.openFirstAnswer,
        );
        if (showFirstAnswer && initialData.questionEntries?.length) {
          questionId = initialData.questionEntries?.[0].id;
        }
      }

      const allProps = {
        instance,
        instanceId,
        baseURL,
        questionId,
        initialData,
        fromSearch,
        api: {
          getListCategories,
          getListQuestions,
          getSearchTerm,
        },
        fitToContentHeight: true,
        isSEO,
        isEditor3,
      };

      controllerConfig.setProps(allProps);
    } catch (error) {
      console.log('Error in onPageReady:', error);
    }
  };

  return {
    async pageReady($w, wixCodeApi) {
      const query = wixCodeApi?.location?.query ?? {};

      if (query.commonConfig) {
        const commonConfig = JSON.parse(query.commonConfig);
        isEditor3 = `${commonConfig.host}`.toLowerCase() === 'editor3';
      }

      const result = await onPageReady();
      const biLogger = flowAPI.bi;
      try {
        biLogger?.report(faqAppStartLoading({ isOoi: true }));
      } catch (e) {
        console.warn('biLogger report faqAppStartLoading failed');
      }
      return result;
    },
    async updateAppSettings() {
      return onPageReady();
    },
  };
};

export default createController;
